import { useEffect } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ArrowLeftCircle, RefreshCw, Sparkles, Users } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import type { RootState } from '@/redux/store'
import { fetchCharterRoster } from '../../redux/asyncThunks/charterThunks'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import {
  selectTeamStatuses,
  selectUnassignedMembers,
} from '../../redux/selectors/charterSelectors'
import { PrepMonitoringCard } from './PrepMonitoringCard'
import { GroupMonitoringCard } from './GroupMonitoringCard'
import { UnassignedRosterPanel } from './UnassignedRosterPanel'
import ROUTES from '@/routes/routes'

interface CharterMonitoringPanelProps {
  inviteCode?: string
}

export function CharterMonitoringPanel({ inviteCode = '' }: CharterMonitoringPanelProps) {
  const dispatch = useAppDispatch()
  const { roomId = '' } = useParams<{ roomId: string }>()

  const teamStatuses = useAppSelector(selectTeamStatuses)
  const unassignedMembers = useAppSelector(selectUnassignedMembers)
  const currentRoom = useSelector((state: RootState) => state.room.currentRoom)

  const effectiveInviteCode = inviteCode || currentRoom?.invite_code || ''

  useEffect(() => {
    if (roomId) dispatch(fetchCharterRoster({ roomId }))
  }, [dispatch, roomId])

  // Hydrate currentRoom after a hard reload so the navbar Charter tab
  // (charter_enabled) and the invite-code fallback below are available.
  useEffect(() => {
    if (roomId && currentRoom?.roomid !== roomId) {
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId, currentRoom?.roomid])

  const handleRefresh = () => {
    if (roomId) dispatch(fetchCharterRoster({ roomId }))
  }

  // Teams that have a Phase 2 or ratified session
  const groupSessions = teamStatuses.filter(
    (t) =>
      t.session?.phase === 'phase2_group' ||
      t.session?.phase === 'ratified' ||
      t.session?.status === 'renegotiating'
  )

  // Build a map from member_id → team_id for the prep cards
  const memberTeamMap = new Map<number, number>()
  teamStatuses.forEach((ts) => {
    ts.members.forEach((m) => memberTeamMap.set(m.member_id, ts.team_id))
  })

  // Collect all member prep sessions across all teams
  const allMemberPreps = teamStatuses.flatMap((ts) => ts.members)

  return (
    <div className='mt-6 space-y-10'>
      {/* ── Page header — mirrors Monitoring tab ────────────────────────── */}
      <div className='space-y-2'>
        <div className='flex items-center gap-x-2'>
          <NavLink to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)} className='flex-shrink-0'>
            <ArrowLeftCircle className='text-dark-blue hover:text-dark-blue-hovered' />
          </NavLink>
          <h1 className='text-2xl font-bold text-dark-blue'>Charter Monitoring</h1>
        </div>
        <p className='text-lg text-black'>
          Monitor individual prep sessions and team group charter sessions.
        </p>
      </div>

      {/* ── Students with no team yet ───────────────────────────────────── */}
      <UnassignedRosterPanel members={unassignedMembers} />

      {/* ── Preparation Coach Panel ─────────────────────────────────────── */}
      <div>
        <div className='flex items-center gap-2'>
          <Sparkles className='h-5 w-5 text-indigo-500' />
          <h5 className='text-xl font-bold text-dark-blue'>Preparation Coach</h5>
          <button
            onClick={handleRefresh}
            className='bg-transparent cursor-pointer rounded-md p-1 hover:bg-gray-100 transition-colors'
            aria-label='Refresh'
          >
            <RefreshCw className='size-4 text-gray-500' />
          </button>
        </div>
        <p className='text-base text-dark-blue mb-6'>
          Monitor individual Phase 1 preparation sessions.
        </p>

        {allMemberPreps.length > 0 ? (
          <div className='grid grid-cols-[repeat(auto-fill,_minmax(224px,_1fr))] xl:grid-cols-5 gap-9'>
            {allMemberPreps.map((member, idx) => {
              const teamId = memberTeamMap.get(member.member_id) ?? 0
              return (
                <PrepMonitoringCard
                  key={member.member_id}
                  player={member}
                  roomId={roomId}
                  inviteCode={effectiveInviteCode}
                  teamId={teamId}
                  colorIndex={idx}
                />
              )
            })}
          </div>
        ) : (
          <div className='h-[15.6rem] flex justify-center items-center flex-col gap-2'>
            <img
              src='/icons/no-negotiation.svg'
              alt='No Sessions'
              className='h-40 w-40'
            />
            <p className='text-lg text-dark-blue'>No charter sessions found</p>
          </div>
        )}
      </div>

      {/* ── Group Moderator Panel ────────────────────────────────────────── */}
      {groupSessions.length > 0 && (
        <div>
          <div className='flex items-center gap-2 mt-4'>
            <Users className='h-5 w-5 text-indigo-500' />
            <h5 className='text-xl font-bold text-dark-blue'>Group Moderator</h5>
          </div>
          <p className='text-base text-dark-blue mb-6'>
            Monitor team group charter sessions (Phase 2).
          </p>

          <div className='grid grid-cols-[repeat(auto-fill,_minmax(224px,_1fr))] xl:grid-cols-5 gap-9'>
            {groupSessions.map((teamStatus, idx) => (
              <GroupMonitoringCard
                key={teamStatus.team_id}
                teamStatus={teamStatus}
                roomId={roomId}
                inviteCode={effectiveInviteCode}
                colorIndex={idx + allMemberPreps.length}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
